import React, { useState, useReducer, useEffect } from 'react';
import { WithStyles } from '@material-ui/core/styles';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import { withRouter, RouteComponentProps } from 'react-router-dom';

import { useMutation, useQuery } from '@apollo/client';

import { withStyles } from '@material-ui/core';
import { useSnackbar } from 'notistack';

import CustomBackdrop from '../../../../components/CustomBackdrop';
import { initialOpeningHours, initialPreorderHours, InventoryRestockType, LocationMenu, locationReducer, LocationType } from '../Common';
import Steps from '../Common/components/StepsNew';

import { createLocationInitQuery } from './queries';
import { insertLocationMutation, InsertLocationMutationResult } from './mutations';

import styles from './styles';
import { getFileRequest, webhookClient } from '../../../../utils/webhookClient';
import { Dashboard } from '../../../../layouts';
import { Breadcrum } from '../../../../layouts/Dashboard/components/Topbar';
import { compressImage, getDataUrlSizeInMB } from '../../../../utils/imageCompression';

interface Props extends WithStyles<typeof styles>, RouteComponentProps {
  classes: ClassNameMap<string>;
  organisation_id?: string;
  tax_rate: number;
  handleFinish: (id: string) => void;
  setBreadcrumbs: any;
  breadcrumbs: Breadcrum[];
  breadcrumbsUpdated: boolean;
}

const CreateLocation = ({ classes, history, organisation_id, tax_rate = 0, handleFinish, setBreadcrumbs, breadcrumbsUpdated, breadcrumbs }: Props): React.ReactElement => {
  const { enqueueSnackbar } = useSnackbar();
  const [locationTypes, setLocationTypes] = useState<LocationType[]>([]);
  const [inventoryRestockTypes, setInventoryRestockTypes] = useState<InventoryRestockType[]>([]);
  const [locationMenus, setLocationMenus] = useState<LocationMenu[]>([]);

  const [location, dispatchLocation] = useReducer(locationReducer, {
    name: '',
    email: '',
    phone: '',
    website: '',
    tax_number: '',
    number_of_tables: 0,
    location_type: 'RESTAURANT',
    address_line_1: '',
    address_line_2: '',
    address_city: '',
    address_country: '',
    address_postcode: '',
    tax_rate: tax_rate,
    currency: '',
    locale: '',
    can_manage_menus: false,
    has_kitchen_printer: false,
    has_bar_printer: false,
    has_staff: false,
    has_station_printers: false,
    hide_kiosk_table_number: false,
    service_charge: null,
    bag_fee: null,
    idle_timeout: 30,
    location_menu_id: undefined,
    ordering_system_primary_color: '#3b4d19',
    ordering_system_secondary_color: '#7bbf40',
    google_font_family: '',
    google_font_url: '',
    google_font_css: '',
    header_image_added: false,
    logo_image_added: false,
    background_image_added: false,
    background_image: undefined,
    background_image_data: undefined,
    ordering_system_open_hours: initialOpeningHours,
    ordering_system_preorder_hours: initialPreorderHours,
    preorder_days_limit: null,
    preorder_pacing_limit: null,
    order_interval: 15,
    inventory_restock_type: '',
    voucher_codes_aggregate: {
      aggregate: {
        count: 0,
      },
    },
    integrations: {
      vouchers: false,
    },
  });

  const { data: createLocationInitData } = useQuery(createLocationInitQuery(organisation_id), { fetchPolicy: 'no-cache' });

  const afterLocationCreated = async (locationData: InsertLocationMutationResult) => {
    const {
      insert_locations_one: { id: locationId },
    } = locationData;

    const MAX_IMAGE_SIZE_MB = 5;

    try {
      if (location.header_image && location.logo_image) {
        // Validate and compress header image
        let headerImageToUpload = location.header_image;
        const headerSizeMB = getDataUrlSizeInMB(location.header_image);
        if (headerSizeMB > MAX_IMAGE_SIZE_MB) {
          enqueueSnackbar(
            `Header image is too large (${headerSizeMB.toFixed(2)}MB). Maximum size is ${MAX_IMAGE_SIZE_MB}MB.`,
            { variant: 'error' }
          );
          return;
        }
        if (headerSizeMB > 2) {
          enqueueSnackbar('Compressing header image...', { variant: 'info' });
          headerImageToUpload = await compressImage(location.header_image, {
            maxSizeMB: 2,
            maxWidthOrHeight: 1920,
            quality: 0.85,
          });
        }

        // Validate and compress logo image
        let logoImageToUpload = location.logo_image;
        const logoSizeMB = getDataUrlSizeInMB(location.logo_image);
        if (logoSizeMB > MAX_IMAGE_SIZE_MB) {
          enqueueSnackbar(
            `Logo image is too large (${logoSizeMB.toFixed(2)}MB). Maximum size is ${MAX_IMAGE_SIZE_MB}MB.`,
            { variant: 'error' }
          );
          return;
        }
        if (logoSizeMB > 2) {
          enqueueSnackbar('Compressing logo image...', { variant: 'info' });
          logoImageToUpload = await compressImage(location.logo_image, {
            maxSizeMB: 2,
            maxWidthOrHeight: 1920,
            quality: 0.85,
          });
        }

        const headerImageRequest = await getFileRequest(headerImageToUpload, `${locationId}.jpeg`, 'image/jpeg');
        await webhookClient.postFormData('ordering-system/images-upload', headerImageRequest);

        const logoImageRequest = await getFileRequest(logoImageToUpload, `${locationId}-logo.jpeg`, 'image/jpeg');
        await webhookClient.postFormData('ordering-system/images-upload', logoImageRequest);
      }

      if (location.background_image) {
        // Validate and compress background image
        let backgroundImageToUpload = location.background_image;
        const backgroundSizeMB = getDataUrlSizeInMB(location.background_image);
        if (backgroundSizeMB > MAX_IMAGE_SIZE_MB) {
          enqueueSnackbar(
            `Background image is too large (${backgroundSizeMB.toFixed(2)}MB). Maximum size is ${MAX_IMAGE_SIZE_MB}MB.`,
            { variant: 'error' }
          );
          return;
        }
        if (backgroundSizeMB > 2) {
          enqueueSnackbar('Compressing background image...', { variant: 'info' });
          backgroundImageToUpload = await compressImage(location.background_image, {
            maxSizeMB: 2,
            maxWidthOrHeight: 1920,
            quality: 0.85,
          });
        }

        const backgroundImageRequest = await getFileRequest(backgroundImageToUpload, `${locationId}-background.jpeg`, 'image/jpeg');
        await webhookClient.postFormData('ordering-system/images-upload', backgroundImageRequest);
      }

      handleFinish(locationId);
    } catch (error: any) {
      enqueueSnackbar(
        error?.message || 'Failed to upload images. The location was created but images were not uploaded.',
        { variant: 'warning' }
      );
      handleFinish(locationId);
    }
  };

  const [createLocation, { loading: saving }] = useMutation<InsertLocationMutationResult>(insertLocationMutation, {
    onError: () => {},
    onCompleted: (locationData) => afterLocationCreated(locationData),
  });

  useEffect(() => {
    if (createLocationInitData) {
      setLocationTypes(createLocationInitData.enum_location_types);
      setInventoryRestockTypes(createLocationInitData.enum_inventory_restock_types);
      setLocationMenus(createLocationInitData.menus);
    }
  }, [createLocationInitData]);

  const handleSave = async () => {
    const {
      header_image_added,
      header_image,
      header_image_data,
      logo_image_added,
      logo_image,
      logo_image_data,
      background_image_added,
      background_image,
      background_image_data,
      voucher_codes_aggregate,
      integrations,
      ...rest
    } = location;

    const insertLocationVariables = {
      object: {
        organisation_id,
        ...rest,
        header_image_added: !!header_image,
        logo_image_added: !!logo_image,
        background_image_added: !!background_image,
        integrations: {
          data: {
            organisation_id,
            previous_report_at: new Date().toISOString(),
          },
        },
      },
    };
    await createLocation({ variables: insertLocationVariables });
  };

  const [step, setStep] = useState<number>(0);

  useEffect(() => {
    if (breadcrumbsUpdated) {
      //@ts-ignore
      setBreadcrumbs((prev) => [...prev, { label: 'Location settings', action: handleResetSteps }]);
    }
    // eslint-disable-next-line
  }, [breadcrumbsUpdated]);
  const handleResetSteps = () => {
    setStep(0);
    //@ts-ignore
    setBreadcrumbs((prevState) => {
      return prevState.slice(0, -1);
    });
  };

  return (
    <Dashboard breadcrumbs={breadcrumbs}>
      <Steps
        completeLabel="Create"
        location={location}
        locationId={undefined}
        organisationId={undefined}
        locationTypes={locationTypes}
        inventoryRestockTypes={inventoryRestockTypes}
        locationMenus={locationMenus}
        dispatch={dispatchLocation}
        handleSave={handleSave}
        isLocationCreate
        setBreadcrumbs={setBreadcrumbs}
        step={step}
        setStep={setStep}
      />
      {saving && <CustomBackdrop label="Creating Location" />}
    </Dashboard>
  );
};

export default withRouter(withStyles(styles)(CreateLocation));
